/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { Box, Button, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import { DataGridPro, GridCallbackDetails, GridColDef, GridRowId, GridRowParams } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf as faFilePdfLight, faListCheck, faLockAlt } from '@fortawesome/pro-light-svg-icons';
import { faFilePdf as faFilePdfSolid } from '@fortawesome/pro-solid-svg-icons';
import { FasitDataGridToolbar } from '../../../../../components/data-grid/fasit-data-grid-toolbar';
import { CitizenCvItem, CvSearchMunicipality } from '../../../../../api-service/clients';
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import { useIsAllowed } from '../../../../../authentication/use-is-allowed';
import { ResourceOperations, ResourceTypes } from '../../../../../authentication/resources';
import { joinURL, win } from '../../../../../utilities';
import { citizenAreaPathName, citizenDetailsTabPathName } from '../../citizen-authority-area-pathnames';
import { FormDialog } from '../../../../../components/form-dialog';
import { CvResultDetails } from './cv-result-details';
import { ColorPalette } from '../../../../../components/config';
import { isNullOrUndefined } from '../../../../../utilities/null-undefined-helper';

const citizenClient = moduleClientFactory.createCitizenClient();

function getCitizenLookupDialogContent(hasReadAccess: boolean, hasWriteAccess: boolean) {
  if (!hasReadAccess) {
    return {
      title: 'Manglende rettigheder',
      text: 'Du har ikke rettigheder til at slå borgeren op.',
    };
  }
  if (!hasWriteAccess) {
    return {
      title: 'Manglende rettigheder',
      text: 'Personen med dette CPR-nummer har ikke nogen tidligere eller igangværende sager i jobcentret. Du har ikke rettigheder til at oprette borgeren.',
    };
  }
  return {
    title: 'Borgeropslag',
    text: 'Personen med dette CPR-nummer har ikke nogen tidligere eller igangværende sager i jobcentret. Ønsker du at få oprettet borgeren i systemet?',
  };
}

export function CvResultPanel(props: ICvResultPanelProps) {
  const {
    citizenCvs,
    openedCvCprs,
    municipalities,
    totalCvs,
    selectionModel,
    currentPageNumber,
    isLoadingMore,
    pageSize,
    onSelectionModelChanged,
    openCitizenCv,
    onFurtherSearchClick,
    onPageChange,
    onPageSizeChange,
  } = props;
  const [citizenCpr, setCitizenCpr] = useState<string | null>(null);
  const hasCitizenReadAccess = useIsAllowed([{ resourceType: ResourceTypes.Citizen, resourceOperation: ResourceOperations.Read }]);
  const hasCitizenWriteAccess = useIsAllowed([{ resourceType: ResourceTypes.Citizen, resourceOperation: ResourceOperations.Write }]);
  const [isLoading, setIsLoading] = useState(false);
  const canCreateCitizen = hasCitizenReadAccess && hasCitizenWriteAccess;

  const { title, text } = getCitizenLookupDialogContent(hasCitizenReadAccess, hasCitizenWriteAccess);

  function getCitizenRedirectUrl(citizenId: string | undefined) {
    return citizenId ? joinURL(citizenAreaPathName, citizenId, citizenDetailsTabPathName) : undefined;
  }

  async function handleDataGridRowClick(params: GridRowParams) {
    const row = params.row as CitizenCvItem;
    if (hasCitizenReadAccess) {
      try {
        const citizen = await citizenClient.getByCpr({ citizenCpr: row.citizenCpr });

        if (citizen.statusCode === 200) {
          const getOrAddResponse = await citizenClient.getOrAddCitizen({ citizenCpr: row.citizenCpr });
          const citizenId = getOrAddResponse?.citizenId;
          const citizenRedirectLink = getCitizenRedirectUrl(citizenId);

          if (citizenRedirectLink) {
            win.open(citizenRedirectLink);
          }
          return;
        }
      } catch (error) {
        // handle Error
      }
    }

    setCitizenCpr(row.citizenCpr || null);
  }

  async function handleNewCitizen() {
    try {
      setIsLoading(true);
      if (canCreateCitizen) {
        const cpr = citizenCpr ?? undefined;
        const getOrAddResponse = await citizenClient.getOrAddCitizen({ citizenCpr: cpr });
        const citizenId = getOrAddResponse?.citizenId;
        const citizenRedirectLink = getCitizenRedirectUrl(citizenId);

        if (citizenRedirectLink) {
          win.open(citizenRedirectLink);
        }
      }
      setCitizenCpr(null);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function getDetailPanel(item: CitizenCvItem) {
    return <CvResultDetails item={item} />;
  }

  return (
    <>
      <DataGridPro<CitizenCvItem>
        loading={isLoadingMore}
        columns={getColumns(openCitizenCv, openedCvCprs, municipalities)}
        rows={citizenCvs || []}
        rowCount={totalCvs}
        onRowClick={handleDataGridRowClick}
        autoHeight
        density="compact"
        getRowId={row => row.citizenCpr || ''}
        components={{
          Toolbar: () => CustomToolbar(selectionModel, onFurtherSearchClick, totalCvs),
        }}
        pagination
        page={currentPageNumber}
        paginationMode="server"
        onPageChange={onPageChange}
        pageSize={pageSize}
        rowsPerPageOptions={[20, 50, 100]}
        onPageSizeChange={onPageSizeChange}
        getDetailPanelContent={({ row }) => getDetailPanel(row)}
        getDetailPanelHeight={() => 'auto'}
        checkboxSelection
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChanged}
        disableColumnMenu
      />
      <FormDialog
        open={!!citizenCpr}
        title={title}
        onConfirmClick={handleNewCitizen}
        onCancelClick={canCreateCitizen ? () => setCitizenCpr(null) : undefined}
        isLoading={isLoading}
      >
        {text}
      </FormDialog>
    </>
  );
}

interface ICvResultPanelProps {
  citizenCvs: CitizenCvItem[];
  totalCvs?: number;
  openedCvCprs: string[];
  municipalities: CvSearchMunicipality[];
  openCitizenCv: (citizenCpr: string) => void;
  onSelectionModelChanged: (selectionModelChange: GridRowId[]) => void;
  selectionModel: GridRowId[];
  onFurtherSearchClick: () => void;
  currentPageNumber: number;
  onPageChange: (page: number, details: GridCallbackDetails<any>) => void;
  isLoadingMore: boolean;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
}

function CustomToolbar(selectionModel: GridRowId[], onFurtherSearchClick: () => void, totalRecords?: number) {
  return FasitDataGridToolbar({
    title: `CV søgeresultater - ${!isNullOrUndefined(totalRecords) && `${totalRecords} i alt`}`,
    showColumnButton: false,
    showDensityButton: false,
    showFilterButton: false,
    customInput: selectionModel?.length > 0 && (
      <Tooltip title="Søg videre i egne borgere">
        <Button onClick={onFurtherSearchClick} startIcon={<FontAwesomeIcon icon={faListCheck} />} />
      </Tooltip>
    ),
  });
}

function getColumns(openCitizenCv: (citizenCpr: string) => void, openedCvCprs: string[], municipalities: CvSearchMunicipality[]): GridColDef<CitizenCvItem>[] {
  return [
    {
      field: 'citizenName',
      headerName: 'Borger',
      width: 250,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          {`${row.firstName} ${row.lastName}`}
          {row.hasNameAndAddressProtection && (
            <Tooltip title="Borger har adressebeskyttelse">
              <FontAwesomeIcon icon={faLockAlt} color={ColorPalette.HotAurora} />
            </Tooltip>
          )}
        </Box>
      ),
      sortable: false,
    },
    {
      field: 'cvFile',
      headerName: 'CV',
      width: 50,
      renderCell: ({ row }) => {
        const { citizenCpr } = row;
        if (citizenCpr) {
          if (!openedCvCprs.some(x => x === citizenCpr)) {
            return (
              <FontAwesomeIcon
                icon={faFilePdfLight}
                size="lg"
                onClick={event => {
                  event.stopPropagation();
                  openCitizenCv(citizenCpr);
                }}
              />
            );
          }
          return (
            <Box>
              <FontAwesomeIcon
                icon={faFilePdfSolid}
                size="xl"
                color="#009908"
                onClick={event => {
                  event.stopPropagation();
                  openCitizenCv(citizenCpr);
                }}
              />
            </Box>
          );
        }
        return <div />;
      },
      sortable: false,
    },
    {
      field: 'jobWishes',
      headerName: 'Jobønsker',
      width: 250,
      renderCell: ({ row }) => (
        <Tooltip
          placement="left-start"
          title={
            <List dense>
              {row.jobWishes?.map(x => (
                <ListItem key={x.jobWishCode} sx={{ paddingY: 0 }}>
                  <ListItemText primary={x.jobWish} />
                </ListItem>
              ))}
            </List>
          }
        >
          <span>{`${row.jobWishes ? row.jobWishes?.map(x => x.jobWish).join(', ') : ''}`}</span>
        </Tooltip>
      ),
      valueGetter: ({ row }) => row.jobWishes?.map(x => x.jobWish).join(', '),
      sortable: false,
    },
    {
      field: 'educations',
      headerName: 'Uddannelser',
      flex: 1,
      sortable: false,
    },
    {
      field: 'city',
      headerName: 'Bopæl',
      width: 150,
      valueGetter: ({ row }) => `${row.postalCode} ${row.city}`,
      sortable: false,
    },
    {
      field: 'cvIsSearchable',
      headerName: 'CV søgbart',
      width: 150,
      valueFormatter: ({ value }) => (value ? 'Ja' : 'Nej'),
      sortable: false,
    },
    {
      field: 'municipalityNumber',
      headerName: 'Kommune',
      width: 150,
      valueFormatter: ({ value }) => municipalities.find(x => Math.floor(value / 100).toString() === x.externalIdentifier)?.name,
      sortable: false,
    },
  ];
}
